import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MapPin, Package, Mail, Phone, Star, Send, Truck } from "lucide-react";
import SaveButton from "@/components/SaveButton";
import ReportDialog from "@/components/ReportDialog";
import VerifiedBadge from "@/components/VerifiedBadge";
import BiddingWidget from "@/components/BiddingWidget";

const PLACEHOLDER = "https://images.unsplash.com/photo-1572521165329-b197f9ea3da6?crop=entropy&cs=srgb&fm=jpg&w=1400&q=70&ixlib=rb-4.1.0";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [active, setActive] = useState(0);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setListing(data)).catch(() => {
      toast.error("Listing not found");
      navigate("/listings");
    });
  }, [id, navigate]);

  const sendMessage = async () => {
    if (!user) { navigate("/login"); return; }
    if (!msg.trim()) return;
    try {
      await api.post("/messages", {
        listing_id: listing.listing_id,
        recipient_id: listing.seller_id,
        body: msg.trim(),
      });
      toast.success("Message sent");
      setMsgOpen(false);
      setMsg("");
      navigate("/dashboard/messages");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  if (!listing) return <div className="mx-auto max-w-7xl px-6 py-16">Loading…</div>;

  const images = listing.images?.length ? listing.images : [];
  const cover = images[active] ? fileUrl(images[active]) : PLACEHOLDER;
  const isOwn = user?.user_id === listing.seller_id;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
      <Link to="/listings" className="overline text-muted-foreground hover:text-forest">← Back to marketplace</Link>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-8">
        {/* Left – gallery */}
        <div>
          <div className="aspect-[4/3] border border-border bg-sage overflow-hidden">
            <img
              src={cover}
              alt={listing.title}
              onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
              className="w-full h-full object-cover"
            />
          </div>
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-6 gap-2">
              {images.map((p, i) => (
                <button
                  key={p}
                  onClick={() => setActive(i)}
                  data-testid={`listing-thumb-${i}`}
                  className={`aspect-square border overflow-hidden ${i === active ? "border-forest" : "border-border"}`}
                >
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8 border border-border bg-white p-6 lg:p-8">
            <div className="overline text-safety">Description</div>
            <p className="mt-4 leading-relaxed whitespace-pre-line text-sm text-foreground/90">{listing.description}</p>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 pt-6 border-t border-border">
              <Meta label="Category" value={listing.category} />
              <Meta label="Condition" value={listing.condition} />
              <Meta label="Quantity" value={listing.quantity} />
              <Meta label="Listed" value={new Date(listing.created_at).toLocaleDateString("en-GB")} />
            </div>
          </div>
        </div>

        {/* Right — sticky action */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="border border-border bg-white p-6 lg:p-8">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {listing.status === "pending" && <Badge className="rounded-none bg-safety text-white overline" data-testid="listing-status-pending">Pending review</Badge>}
              {listing.status === "flagged" && <Badge className="rounded-none bg-destructive text-white overline">Flagged</Badge>}
              {listing.is_featured && <Badge className="rounded-none bg-forest text-white overline">Featured</Badge>}
              {listing.is_clearance && <Badge className="rounded-none bg-safety text-white overline">Clearance</Badge>}
              <Badge variant="outline" className="rounded-none border-border">{listing.seller_type.replace("_", " ")}</Badge>
              <div className="ml-auto">
                <SaveButton
                  listingId={listing.listing_id}
                  testId={`listing-detail-save`}
                  className="w-10 h-10 border border-border hover:border-safety text-forest"
                  unsavedColorClass="text-forest"
                />
              </div>
            </div>
            <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter text-forest leading-tight">
              {listing.title}
            </h1>
            <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4" /> {listing.location}{listing.postcode ? ` · ${listing.postcode}` : ""}
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <div className="overline text-muted-foreground">Price</div>
              <div className="mt-1 font-display font-black text-5xl text-forest tabular-nums">
                £{Number(listing.price).toFixed(2)}
              </div>
              {listing.quantity > 1 && (
                <div className="mt-1 text-sm text-muted-foreground">£{(listing.price * listing.quantity).toFixed(2)} for all {listing.quantity} items</div>
              )}
            </div>
              <BiddingWidget listing={listing} isOwn={isOwn} />

            {!isOwn && (
              <div className="mt-6 space-y-3">
                <Button
                  onClick={() => (user ? setMsgOpen(true) : navigate("/login"))}
                  data-testid="listing-message-btn"
                  className="w-full rounded-none bg-forest hover:bg-forest-800 text-white h-12"
                >
                  <Send className="w-4 h-4 mr-2" /> Message seller
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="listing-book-removal-btn"
                  className="w-full rounded-none border-safety text-safety hover:bg-safety hover:text-white h-12"
                >
                  <Link to="/removal-companies"><Truck className="w-4 h-4 mr-2" /> Book a removal</Link>
                </Button>
              </div>
            )}

            {isOwn && (
              <div className="mt-6">
                <Button
                  asChild
                  data-testid="listing-upgrade-btn"
                  className="w-full rounded-none bg-safety hover:bg-safety-hover text-white h-12"
                >
                  <Link to={`/upgrade/${listing.listing_id}`}>⚡ Boost with a feature upgrade</Link>
                </Button>
              </div>
            )}
          </div>

          <div className="border border-border bg-white p-6">
            <div className="overline text-muted-foreground mb-2">Seller</div>
            <div className="flex items-center gap-2">
              <div className="font-display text-xl tracking-tight text-forest">{listing.seller_name}</div>
              <VerifiedBadge verified={listing.seller_is_verified} size="lg" testId="listing-detail-verified" />
            </div>
            <div className="mt-1 overline text-xs text-muted-foreground">{listing.seller_type.replace("_", " ")}</div>
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              {listing.contact_email && <div className="flex items-center gap-2"><Mail className="w-4 h-4" /> {listing.contact_email}</div>}
              {listing.contact_phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {listing.contact_phone}</div>}
            </div>
            {!isOwn && (
              <div className="mt-4 pt-4 border-t border-border">
                <ReportDialog targetType="listing" targetId={listing.listing_id} testId="report-listing" label="Report this listing" />
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Message dialog */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="bg-white rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Message {listing.seller_name}</DialogTitle>
          </DialogHeader>
          <Textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={5}
            placeholder="Is this still available? I can collect from…"
            data-testid="listing-message-input"
            className="rounded-none"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgOpen(false)} className="rounded-none">Cancel</Button>
            <Button
              onClick={sendMessage}
              data-testid="listing-message-send"
              className="rounded-none bg-forest hover:bg-forest-800 text-white"
            >
              Send message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="overline text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium">{value}</div>
    </div>
  );
}
